import { useEffect, useState } from "react";
import moment from "moment";
import customAxios from "../../../../utils/customAxios";
import {
    normalizeTerminalValue,
    getLocationFromFormattedAddress,
    mapPaxToState,
    mapLuggageToState,
    mapPaxToStateNew,
    mapLuggageToStateNew,
    mapVehiclesToState,
    extractMessage,
    parseTitleAndName,
} from "../utils";

export function useBookingDetail({
    id,
    lang,
    setTransferType,
    setTransferFlags,
    setPickupLocation,
    setPickupLocationData,
    setDropoffLocation,
    setDropoffLocationData,
    setAirportDropoffLocations,
    setTravelDate,
    setFormatedTravelDate,
    setPickupTime,
    setDuration,
    setPassengers,
    setLuggage,
    setVehicles,
    setChildSeatAllocations,
    setBookingFor,
    setPassengerName,
    setCustomer,
    setBookingKey,
    setPrefixPhoneNumber,
    setPhoneNumber,
    setCountryCode,
    setMeetAssistS,
    setPickupFlightNo,
    setDropoffFlightNo,
    setPickupTerminal,
    setDropoffTerminal,
    setPickupSignHonorific,
    setPickupSign,
    setMessage,
    setTravelArea,
}) {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchBookingDetail = async () => {
            if (!id) return;

            setIsLoading(true);
            try {
                const queryString = new URLSearchParams({
                    id,
                    isAbandoned: "false",
                }).toString();

                const response = await customAxios.get(`/api/auth/bookings?${queryString}`);
                const bookingDetail = response?.data?.data?.data?.[0];

                if (!bookingDetail) return;

                const transferTypeValue = bookingDetail?.bookingType === "byTheHour" ? "charter" : "airport";
                setTransferType(transferTypeValue);
                setTransferFlags({
                    isAirportTransfer: Boolean(bookingDetail?.isAirportTransfer),
                    isFromAirport: Boolean(bookingDetail?.isFromAirport),
                    isToAirport: Boolean(bookingDetail?.isToAirport),
                    isStationTransfer: Boolean(bookingDetail?.isStationTransfer),
                    isFromStation: Boolean(bookingDetail?.isFromStation),
                    isToStation: Boolean(bookingDetail?.isToStation),
                });

                const pickupAddress = getLocationFromFormattedAddress(
                    bookingDetail?.pickupLocation?.formatted_address
                );
                const destinationAddresses = (bookingDetail?.destinations || [])
                    .map((destination) =>
                        getLocationFromFormattedAddress(destination?.formatted_address)
                    )
                    .filter(Boolean);

                setPickupLocation(pickupAddress || "");
                setPickupLocationData(bookingDetail?.pickupLocation || null);

                if (destinationAddresses.length > 0) {
                    setDropoffLocation(destinationAddresses[0]);
                    setDropoffLocationData(bookingDetail?.destinations?.[0] || null);
                    setAirportDropoffLocations(
                        (bookingDetail?.destinations || []).map((destination, index) => ({
                            id: index + 1,
                            address: destination?.formatted_address || "",
                            data: destination || null,
                        }))
                    );
                } else {
                    setDropoffLocation("");
                    setDropoffLocationData(null);
                    setAirportDropoffLocations([{ id: 1, address: "", data: null }]);
                }

                const pickupDateObj = bookingDetail?.pickupTime
                    ? new Date(bookingDetail.pickupDateTime)
                    : null;
                if (pickupDateObj && !Number.isNaN(pickupDateObj.getTime())) {
                    setTravelDate(pickupDateObj);
                    setFormatedTravelDate(moment(pickupDateObj).format("YYYY-MM-DD"));
                    setPickupTime(moment(pickupDateObj).format("HH:mm"));
                }

                if (bookingDetail?.duration) {
                    const durationString = String(bookingDetail.duration);
                    const [hoursRaw = "0", minutesRaw = "0"] = durationString.split(":");
                    const hours = String(Number(hoursRaw) || 0).padStart(2, "0");
                    const minutes = String(Number(minutesRaw) || 0).padStart(2, "0");
                    setDuration({
                        value: `${hours}:${minutes}`,
                        label:
                            lang === "EN" || lang === "en"
                                ? `${hours} Hours ${minutes} Minutes`
                                : `${hours}時間 ${minutes}分`,
                    });
                }

                const mappedVehicles = mapVehiclesToState(bookingDetail?.bookingInfo);
                setPassengers(mapPaxToStateNew(bookingDetail));
                setLuggage(mapLuggageToStateNew(bookingDetail));
                setVehicles(mappedVehicles);

                setChildSeatAllocations(
                    mappedVehicles.map((vehicle, index) => {
                        const bookingInfoItem = bookingDetail?.bookingInfo?.[index] || {};
                        return {
                            infant: Number(bookingInfoItem?.childSeat[0]?.childSeatCount || 0),
                            toddler: Number(bookingInfoItem?.childSeat[1]?.childSeatCount || 0),
                            child: Number(bookingInfoItem?.childSeat[2]?.childSeatCount || 0),
                        };
                    })
                );

                const passengerFullName = bookingDetail?.passengerName || "";
                const customerFullName =
                    `${bookingDetail?.customer?.pronounced || ""} ${bookingDetail?.customer?.name || ""}`.trim();
                setBookingFor(
                    passengerFullName &&
                        customerFullName &&
                        passengerFullName.trim() !== customerFullName
                        ? "someone_else"
                        : "myself"
                );
                setPassengerName(passengerFullName || "");

                setCustomer(bookingDetail?.customer || {});
                setBookingKey(bookingDetail?.key || "");

                const passengerPrefix = bookingDetail?.passengerPrefixNumber || "+81";
                setPrefixPhoneNumber(passengerPrefix);
                setPhoneNumber(bookingDetail?.passengerPhoneNumber || "");
                setCountryCode(bookingDetail?.customer?.country_code || "JP");

                const normalizeMeetAssistName = (name) => {
                    const normalized = String(name || "").toLowerCase();
                    if (normalized.includes("vip")) return "vipMeetAssist";
                    if (normalized.includes("meet")) return "meetAssist";
                    return "none";
                };

                const arrivalMeetAssist = bookingDetail?.meetAssist?.find(
                    (item) => item?.meetAssistType === "arrival"
                );
                const departureMeetAssist = bookingDetail?.meetAssist?.find(
                    (item) => item?.meetAssistType === "departure"
                );

                setMeetAssistS([
                    {
                        meetAssistType: "arrival",
                        meetAssistName: normalizeMeetAssistName(arrivalMeetAssist?.meetAssistName),
                        meetAssistPrice: Number(arrivalMeetAssist?.meetAssistPrice || 0),
                    },
                    {
                        meetAssistType: "departure",
                        meetAssistName: normalizeMeetAssistName(departureMeetAssist?.meetAssistName),
                        meetAssistPrice: Number(departureMeetAssist?.meetAssistPrice || 0),
                    },
                ]);

                setPickupFlightNo(bookingDetail?.pickupFlightNo || "");
                setDropoffFlightNo(bookingDetail?.dropoffFlightNo || "");
                setPickupTerminal(normalizeTerminalValue(bookingDetail?.pickupTerminal || ""));
                setDropoffTerminal(normalizeTerminalValue(bookingDetail?.dropoffTerminal || ""));

                const pickupSign = parseTitleAndName(bookingDetail?.pickupSign || "");
                setPickupSignHonorific(pickupSign.title || "Mr.");
                setPickupSign(pickupSign.name || "");

                setMessage(extractMessage(bookingDetail?.message));

                if (bookingDetail?.travelArea) {
                    setTravelArea(bookingDetail.travelArea);
                }
            } catch (error) {
                console.error("Error loading booking detail:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBookingDetail();
    }, [id]);

    return { isLoading };
}
